import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ImageSquare } from '@phosphor-icons/react';
import { useSelector } from 'react-redux';

import { PsButtonTuner, PsModalTuner, PsTextTuner } from 'stencilSetup';
import { EsignatureDto } from 'enrollment/queries/useBenefitPlansQuery/types';
import { FormElement } from 'enrollment/components/Shared/FormElement';
import { selectPCPProviderInfoLogic } from '../PlanCard/components/ChoosePCPDrawer/SearchResultsPCPDrawer/services';
import { AdditionalPlanElectionDataDto } from '../PlanCard/types';

export interface EsignatureModalProps {
  setEsignElectionData: (data: AdditionalPlanElectionDataDto) => void;
  carrierLogoUrl?: string | null;
  carrierName?: string;
  data: EsignatureDto | null;
  planId: number;
  isFinancial?: boolean;
  hideChoosePlanButton?: boolean;
  pcpData?: any;
  setModalRequired?: (value: boolean) => void;
}

export const EsignatureModal = ({
  setEsignElectionData,
  carrierLogoUrl,
  carrierName,
  data,
  planId,
  isFinancial = false,
  hideChoosePlanButton = false,
  pcpData,
  setModalRequired,
}: EsignatureModalProps) => {
  const { t } = useTranslation('enrollment.esignModal');

  const [esignPassword, setEsignPassword] = useState<string>('');
  const [esignCheckbox, setEsignCheckbox] = useState<boolean>(false);
  const theme = useTheme();
  const isScreenSmall = useMediaQuery(theme.breakpoints.down(744));

  const providerInfoLogicSelector = useSelector(selectPCPProviderInfoLogic);

  const pcpDentalData = providerInfoLogicSelector?.storePcpDentalData;

  const handleEsignPassword = (event: any) => {
    setEsignPassword(event.target.value);
    setEsignCheckbox(false);
  };

  const handleEsignCheckbox = (event: any) => {
    setEsignPassword('');
    setEsignCheckbox(event.target.checked);
  };

  useEffect(() => {
    const confirmBtn = document
      .querySelector(`#esignature-modal-${planId}`)
      ?.querySelector('.btn-primary');

    if (esignCheckbox === true || esignPassword !== '') {
      confirmBtn?.removeAttribute('disabled');
    } else {
      confirmBtn?.setAttribute('disabled', 'true');
    }
  }, [esignCheckbox, esignPassword, planId]);

  const handleSubmit = () => {
    if (setModalRequired) {
      setModalRequired(false);
    }
    if (data?.signature_type?.toLowerCase() === 'password') {
      setEsignElectionData({
        plan_id: planId,
        esign_password: esignPassword,
        ...(pcpData && { pcpFormData: pcpDentalData }),
      });
      setEsignPassword(''); //resetting the password if user enters wrong one & clicks on submit
    } else {
      setEsignElectionData({
        plan_id: planId,
        esign_checked: esignCheckbox,
        ...(pcpData && { pcpFormData: pcpDentalData }),
      });
    }
  };

  const handleCloseModal = () => {
    /*resetting password/checkbox on modal dismiss */
    setEsignCheckbox(false);
    setEsignPassword('');
    
    setModalRequired && setModalRequired(false);
  };

  return (
    <>
      {!hideChoosePlanButton && (
        <div className={`${isScreenSmall ? 'mx-auto' : ''}`}>
          <PsButtonTuner
            data-testid={
              isFinancial ? 'submit-contribution' : 'choose-plan-button'
            }
            buttonId={
              isFinancial ? 'submit-contribution' : 'choose-plan-button'
            }
            label={
              isFinancial
                ? t('pages.financial:looksGood')
                : t('enrollment.benefits:choosePlan')
            }
            level="primary"
            otherData={[
              { attribute: 'data-bs-toggle', value: 'modal' },
              {
                attribute: 'data-bs-target',
                value: `#esignature-modal-${planId}`,
              },
              { attribute: 'data-bs-dismiss', value: 'modal' },
            ]}
          ></PsButtonTuner>
        </div>
      )}

      <PsModalTuner
        data-testid={`esignature-modal-${planId}`}
        modalId={`esignature-modal-${planId}`}
        modalTitle={t('title')}
        primaryActive
        primaryButton={t('common:confirm')}
        secondaryButton={t('common:cancel')}
        onPsModalSubmit={handleSubmit}
        onPsModalDismiss={handleCloseModal}
        modalSize="large"
        disablePrimary={true}
        modalStatic
      >
        <Box>
          <div className="d-flex justify-content-end align-items-center mb-3">
            {carrierLogoUrl !== null ? (
              <img
                src={carrierLogoUrl}
                alt={carrierName || t('common:defaultLogoAltText')}
                data-testid="carrier-logo"
              />
            ) : (
              <>
                <ImageSquare size={32} weight="duotone" color="#56638a" />
                <Typography color="#56638a">{carrierName}</Typography>
              </>
            )}
          </div>

          {/* TODO: this will change once we get clarity from Uma */}
          {data?.esign_text && (
            <Typography variant="body1">{data?.esign_text}</Typography>
          )}

          {data?.signature_type?.toLowerCase() === 'password' ? (
            <PsTextTuner
              data-testid="esignature-password"
              inputId="esignature-password"
              name="esignature_password"
              label="Password"
              type="password"
              value={esignPassword}
              onInput={(e) => handleEsignPassword(e as any)}
              required
              otherData={[{ attribute: 'autoComplete', value: 'off' }]}
              allowUnmask
            />
          ) : (
            <FormElement
              data-testid={`esignature-checkbox-${planId}`}
              formField="checkbox"
              inputId={`esignature-checkbox-${planId}`}
              label={
                data?.custom_text ? data?.custom_text : t('termsConditionLabel')
              }
              value={esignCheckbox}
              checked={esignCheckbox}
              onChange={(e) => handleEsignCheckbox(e as any)}
            />
          )}
        </Box>
      </PsModalTuner>
    </>
  );
};
